import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  TableContainer,
  Pagination,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton
} from '@mui/material';
import { Close as CloseIcon, PersonAdd as PersonAddIcon } from '@mui/icons-material';
import CreateAccount from '../CreateAccount';
import { notify } from '../../notifications';
import { apiErrorMessage } from '../reports/edit/editApi';

// Импорт хуков и компонентов
import {
  useUsers,
  useUserFilters,
  usePagination,
  useUserActions,
  useInlineEditing,
  useTableColumns
} from './hooks';
import {
  SearchBar,
  UserTableRow,
  UserDetailDrawer,
  ColumnConfigDialog
} from './components';
import { isActivationButtonDisabled } from './utils/permissions';
import { impersonateAndRedirect } from './utils/impersonation';
import { User, Region } from './types';
import { AssignableRole, fetchRegions, fetchRoles } from './allUsersApi';
import { fetchStaffCounts, StaffCount } from '../../staff';
import { usePermissions } from '../../../utils/usePermissions';

const AllUsers: React.FC = () => {
  // Хуки данных пользователей
  const { users, setUsers, loading, error, refetchUsers, operator } = useUsers();
  const { searchTerm, searchColumn, filteredUsers, setSearchTerm, setSearchColumn } = useUserFilters(users);
  const { currentPage, totalPages, paginatedItems: paginatedUsers, handleChangePage } = usePagination(filteredUsers, searchTerm);
  const { columns, saveColumns, resetToDefaults } = useTableColumns();

  // Состояние диалога настройки колонок
  const [columnConfigOpen, setColumnConfigOpen] = useState(false);

  // Состояние модалки создания пользователя
  const [createUserModalOpen, setCreateUserModalOpen] = useState(false);

  // Вход под сотрудником без подтверждения: право есть у единиц, а вернуться можно одной кнопкой
  const [impersonating, setImpersonating] = useState(false);

  const handleImpersonate = async (target: User) => {
    if (impersonating) return;
    setImpersonating(true);
    try {
      await impersonateAndRedirect(target);
    } catch (err) {
      notify.error(apiErrorMessage(err, 'Не удалось войти под сотрудником'));
      setImpersonating(false);
    }
  };

  // Карточка берет сотрудника из общего списка по id: после перезагрузки списка флаги прав,
  // роль и статусы в ней сразу актуальные
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const selectedUser = useMemo(
    () => (selectedUserId === null ? null : users.find(u => u.id === selectedUserId) || null),
    [users, selectedUserId]
  );

  // Справочники для карточки: регионы и роли (назначаемые помечает сервер)
  const [regions, setRegions] = useState<Region[]>([]);
  const [roles, setRoles] = useState<AssignableRole[]>([]);

  // Сотрудники точек (§44): сервер возвращает только точки, с которыми оператор может работать
  const { hasPerm } = usePermissions();
  const canViewStaff = hasPerm('users.staff.view');
  const [staffCounts, setStaffCounts] = useState<Record<number, StaffCount>>({});

  const loadStaffCounts = useCallback(() => {
    if (!canViewStaff) return;
    fetchStaffCounts()
      .then(setStaffCounts)
      .catch(err => notify.error(apiErrorMessage(err, 'Не удалось загрузить сотрудников точек')));
  }, [canViewStaff]);

  useEffect(() => {
    loadStaffCounts();
  }, [loadStaffCounts]);

  // Пока список не загружен, оператор неизвестен - ничего лишнего не показываем
  const shouldShowIIN = Boolean(operator && operator.canViewIin);
  const canCreate = Boolean(operator && operator.canCreate);
  const canToggleStatus = Boolean(operator && operator.canToggleStatus);

  // Вычисляем активные колонки с учетом ролей и прав доступа
  const activeColumns = useMemo(() => {
    return columns.filter(col => {
      if (!col.visible) return false;
      if (col.id === 'iin' && !shouldShowIIN) return false;
      return true;
    });
  }, [columns, shouldShowIIN]);

  const visibleColumnIds = useMemo(() => activeColumns.map(c => c.id), [activeColumns]);

  // Хуки для действий с пользователями
  const userActions = useUserActions(refetchUsers, setUsers);

  // Хук для быстрого inline-редактирования в таблице
  const inlineEditing = useInlineEditing(users, refetchUsers, setUsers, operator);

  // Справочники нужны только карточке: при ошибке она открывается, но без выбора региона/роли
  useEffect(() => {
    fetchRegions()
      .then(setRegions)
      .catch(err => notify.error(apiErrorMessage(err, 'Не удалось загрузить регионы')));
    fetchRoles()
      .then(setRoles)
      .catch(err => notify.error(apiErrorMessage(err, 'Не удалось загрузить роли')));
  }, []);

  useEffect(() => {
    document.title = 'Список пользователей | Zerde';
  }, []);

  const handleOpenDrawer = useCallback((user: User) => {
    setSelectedUserId(user.id);
    setDrawerOpen(true);
  }, []);

  // В карточке могли создать или заблокировать сотрудников точки - счетчики перечитываются
  const handleCloseDrawer = useCallback(() => {
    setDrawerOpen(false);
    setSelectedUserId(null);
    loadStaffCounts();
  }, [loadStaffCounts]);

  // Показываем полноэкранный лоадер ТОЛЬКО при первой загрузке
  if (loading && users.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px', gap: 2 }}>
        <CircularProgress sx={{ color: '#1570ef' }} size={28} />
        <Typography sx={{ color: '#64748b', fontWeight: 500 }}>Загрузка пользователей...</Typography>
      </Box>
    );
  }

  if (error && users.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', pb: 4 }}>
      {/* Верхняя панель поиска и массовых действий */}
      <SearchBar
        searchTerm={searchTerm}
        searchColumn={searchColumn}
        onSearchTermChange={setSearchTerm}
        onSearchColumnChange={setSearchColumn}
        onMassReportsLock={userActions.massReportsLock}
        onMassPOSLock={userActions.massPOSLock}
        canCreate={canCreate}
        canToggleStatus={canToggleStatus}
        onOpenColumnConfig={() => setColumnConfigOpen(true)}
        onOpenCreateUser={() => setCreateUserModalOpen(true)}
      />

      {/* Единая таблица пользователей в стиле Zerde & Bitrix24 */}
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          mt: 2,
          borderRadius: 3,
          border: '1px solid #e2e8f0',
          overflowX: 'auto',
          background: '#ffffff',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -2px rgba(0, 0, 0, 0.05)'
        }}
      >
        <Table sx={{ minWidth: 900 }}>
          <TableHead sx={{ bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
            <TableRow>
              {activeColumns.map(col => (
                <TableCell
                  key={col.id}
                  align={col.align || 'left'}
                  sx={{
                    fontWeight: 700,
                    color: '#64748b',
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    py: 1.5,
                    width: col.width,
                    minWidth: col.minWidth,
                    pl: col.id === 'user' ? 2 : undefined,
                    pr: col.id === 'actions' ? 3 : undefined
                  }}
                >
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={Math.max(activeColumns.length, 1)} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    Пользователи не найдены
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedUsers.map((user) => (
                <UserTableRow
                  key={user.id}
                  user={user}
                  shouldShowIIN={shouldShowIIN}
                  visibleColumnIds={visibleColumnIds}
                  editingPharmacy={inlineEditing.editingPharmacy}
                  canEditPharmacyNum={inlineEditing.canEditPharmacyNum}
                  onStartEditingPharmacy={inlineEditing.startEditingPharmacy}
                  onSaveNewPharmacyNum={inlineEditing.saveNewPharmacyNum}
                  onPharmacyChange={(newPharmacyNum) =>
                    inlineEditing.setEditingPharmacy(prev => prev ? { ...prev, newPharmacyNum } : null)
                  }
                  onCancelPharmacyEdit={() => inlineEditing.setEditingPharmacy(null)}
                  onSelectUser={handleOpenDrawer}
                  // Без права users.toggle_status кнопок статусов нет; сотрудник вне зоны - кнопки заблокированы
                  onToggleActivation={canToggleStatus ? userActions.toggleActivation : undefined}
                  onTogglePOS={canToggleStatus ? userActions.togglePOS : undefined}
                  onToggleReports={canToggleStatus ? userActions.toggleReports : undefined}
                  isActivationDisabled={isActivationButtonDisabled(user)}
                  onImpersonate={handleImpersonate}
                  staffCount={staffCounts[user.id]}
                />
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Пагинация */}
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Pagination
          count={totalPages}
          page={currentPage}
          onChange={handleChangePage}
          color="primary"
          shape="rounded"
        />
      </Box>

      {/* Bitrix24 Slide-over Drawer для пользователя */}
      <UserDetailDrawer
        open={drawerOpen}
        onClose={handleCloseDrawer}
        user={selectedUser}
        regions={regions}
        roles={roles}
        showStaff={Boolean(selectedUser && staffCounts[selectedUser.id])}
        onRefetchUsers={refetchUsers}
        onUpdateUser={(updatedFields) => {
          if (selectedUserId !== null) {
            setUsers(prev => prev.map(u => u.id === selectedUserId ? { ...u, ...updatedFields } : u));
          }
        }}
        onToggleActivation={userActions.toggleActivation}
        onTogglePOS={userActions.togglePOS}
        onToggleReports={userActions.toggleReports}
        onDeleteUser={userActions.deleteUser}
      />

      {/* Диалог настройки колонок */}
      <ColumnConfigDialog
        open={columnConfigOpen}
        onClose={() => setColumnConfigOpen(false)}
        columns={columns}
        onSave={saveColumns}
        onResetToDefaults={resetToDefaults}
        shouldShowIIN={shouldShowIIN}
      />

      {/* Модальное окно создания пользователя */}
      <Dialog
        open={createUserModalOpen}
        onClose={() => setCreateUserModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '20px',
            p: 1,
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)'
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1.5, borderBottom: '1px solid #e2e8f0' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: '10px',
                bgcolor: '#eff6ff',
                color: '#1570ef',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <PersonAddIcon sx={{ fontSize: 22 }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a', lineHeight: 1.2 }}>
                Создание пользователя
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={() => setCreateUserModalOpen(false)} size="small" sx={{ color: '#64748b' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3, pt: 3 }}>
          <CreateAccount
            onSuccess={() => {
              setCreateUserModalOpen(false);
              refetchUsers();
              notify.success('Пользователь успешно создан!');
            }}
            onCancel={() => setCreateUserModalOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default AllUsers;